import { apiErrorSchema, type ErrorCode } from '@crm/contracts';
import type { z } from 'zod';

/** A failed BFF call; ``code`` is stable, ``message`` is safe to show. */
export class ApiClientError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode | 'network',
    message: string,
    readonly requestId: string | null = null,
    readonly fields: Record<string, string> = {},
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

let csrfToken = '';

/** The session's CSRF token, sent with every state-changing request. */
export function setCsrfToken(token: string): void {
  csrfToken = token;
}

interface RequestOptions<T> {
  body?: unknown;
  signal?: AbortSignal;
  schema?: z.ZodType<T>;
}

type Method = 'GET' | 'POST' | 'PUT' | 'DELETE';

export async function request<T = void>(
  method: Method,
  path: string,
  options: RequestOptions<T> = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (method !== 'GET') headers['X-CSRF-Token'] = csrfToken;
  // A file upload goes as multipart; the browser sets the boundary itself.
  const form = options.body instanceof FormData;
  if (options.body !== undefined && !form) headers['Content-Type'] = 'application/json';
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      method,
      headers,
      credentials: 'same-origin',
      ...(options.body === undefined
        ? {}
        : { body: form ? (options.body as FormData) : JSON.stringify(options.body) }),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiClientError(0, 'network', 'Нет соединения с сервером. Проверьте сеть');
  }
  if (!response.ok) throw await readError(response);
  if (response.status === 204 || !options.schema) return undefined as T;
  return options.schema.parse(await response.json());
}

async function readError(response: Response): Promise<ApiClientError> {
  const requestId = response.headers.get('x-request-id');
  const parsed = apiErrorSchema.safeParse(await response.json().catch(() => null));
  if (!parsed.success) {
    return new ApiClientError(response.status, 'internal', 'Что-то пошло не так', requestId);
  }
  const { code, message, fields } = parsed.data.error;
  return new ApiClientError(
    response.status,
    code,
    message,
    parsed.data.error.requestId,
    fields ?? {},
  );
}

export function isApiError(error: unknown, code?: ErrorCode): error is ApiClientError {
  return error instanceof ApiClientError && (code === undefined || error.code === code);
}
